import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  AlertTitle,
  IconButton
} from '@mui/material';
import { 
  VisibilityOff as LeftEyeIcon, 
  Visibility as RightEyeIcon,
  InfoOutlined as InfoIcon,
  LockOutlined as LockIcon,
  Apple as AppleIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
// import { addDoc, collection, getFirestore, serverTimestamp } from 'firebase/firestore';
// TODO: Migrate data writing to Realtime Database
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import AmslerGridTest from '../components/AmslerGridTest';
import { getDatabase, ref, set } from 'firebase/database';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';
import TestResultDialog from '../components/TestResultDialog';
import { downloadTestResultImage, shareTestResult, printTestResult } from '../utils/testResultUtils';

const steps = ['Select Eye', 'Take Test', 'Report Findings', 'Notes'];

interface PointCoordinates {
  x: number;
  y: number;
  intensity?: number;
  distortionRadius?: number;
}

const TestPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [eyeSelection, setEyeSelection] = useState<'left' | 'right' | ''>('');
  const [dots, setDots] = useState<PointCoordinates[]>([]);
  const [abnormalities, setAbnormalities] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [abnormalityOptions, setAbnormalityOptions] = useState({
    distortion: false,
    blind_spot: false,
    wavy_lines: false,
    blurred_vision: false,
    other: false
  });
  const [otherAbnormality, setOtherAbnormality] = useState('');
  const [bypassLoading, setBypassLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isPremium, isLoading: subscriptionLoading, verifyBeforeSavingTest } = useSubscription();
  const { firebaseService } = useFirebaseService();

  // Check localStorage for subscription details on mount, but don't rely on it for critical operations
  useEffect(() => {
    if (subscriptionLoading) {
      try {
        const storedData = localStorage.getItem('subscriptionDetails');
        if (storedData) {
          const subData = JSON.parse(storedData);
          const now = Date.now();
          const lastUpdated = subData.lastUpdated || 0;
          
          // Only use cached data if it's less than 1 hour old
          if (now - lastUpdated < 60 * 60 * 1000) {
            console.log('Using cached subscription data from localStorage (< 1 hour old)');
            
            // If user has an active subscription that hasn't expired, skip loading
            if (subData.isPremium === true && 
                subData.subscription?.periodEnd && 
                new Date(subData.subscription.periodEnd).getTime() > now) {
              
              // Allow access to test without waiting for verification
              console.log('Found valid subscription in localStorage - bypassing loading state');
              setBypassLoading(true);
            } else if (subData.isPremium === false) {
              // If we know the user doesn't have premium, show that immediately
              console.log('User does not have premium according to localStorage');
              setBypassLoading(false);
            }
          } else {
            console.log('Cached subscription data is too old, waiting for fresh data');
          }
        }
      } catch (error) {
        console.error('Error reading subscription data from localStorage:', error);
      }
    }
  }, [subscriptionLoading]);

  const handleNext = () => {
    if (activeStep === 0 && !eyeSelection) {
      setError('Please select which eye you want to test');
      return;
    }
    
    if (activeStep === 2) {
      // Collect abnormalities from checkboxes
      const selectedAbnormalities: string[] = [];
      if (abnormalityOptions.distortion) selectedAbnormalities.push('Distortion');
      if (abnormalityOptions.blind_spot) selectedAbnormalities.push('Blind Spot');
      if (abnormalityOptions.wavy_lines) selectedAbnormalities.push('Wavy Lines');
      if (abnormalityOptions.blurred_vision) selectedAbnormalities.push('Blurred Vision');
      if (abnormalityOptions.other && otherAbnormality.trim()) {
        selectedAbnormalities.push(otherAbnormality.trim());
      }
      
      setAbnormalities(selectedAbnormalities);
    }
    
    if (activeStep === steps.length - 1) {
      handleSubmitTest();
      return;
    }
    
    setActiveStep((prevStep) => prevStep + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleEyeSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEyeSelection(event.target.value as 'left' | 'right');
    setError(null);
  };

  const handleMarkPoint = (point: PointCoordinates) => {
    setDots((prevDots) => [...prevDots, point]);
  };

  const handleResetPoints = () => {
    setDots([]);
  };

  const handleAbnormalityChange = (option: keyof typeof abnormalityOptions) => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
    setAbnormalityOptions({
      ...abnormalityOptions,
      [option]: event.target.checked
    });
  };

  const handleSubmitTest = async () => {
    if (!currentUser) {
      setError('You must be logged in to submit a test');
      return;
    }
    
    setLoading(true);
    setError(null);
    console.log('Starting test submission process...');
    
    try {
      // Verify subscription status before saving - this is one of the only places
      // where we should do a full verification
      console.log('Verifying subscription status before saving test...');
      const canSaveTest = await verifyBeforeSavingTest();
      console.log('Subscription verification result:', canSaveTest);
      
      if (!canSaveTest) {
        // Don't try to verify again if we already know the subscription is expired
        // This helps prevent verification loops
        setError('Your subscription has expired. Please renew your subscription to save test results.');
        setLoading(false);
        return;
      }
      
      // Format the abnormalities
      const finalAbnormalities = abnormalities.length > 0 
        ? abnormalities 
        : dots.length > 0 
          ? ['Unspecified abnormality'] 
          : [];
          
      // Create the test result object
      const testResult = {
        userId: currentUser.uid,
        timestamp: Date.now(),
        eyeTested: eyeSelection,  // Use only eyeTested field for compatibility with mobile apps
        dots: dots,              // Use only dots field for compatibility with mobile apps
        abnormalities: finalAbnormalities, // Web-specific feature not in mobile apps yet
        notes: notes.trim(),
      };
      
      console.log('Test result object prepared:', JSON.stringify(testResult));
      
      // Save to Realtime Database
      try {
        console.log('Saving test result to database...');
        console.log('Current user ID:', currentUser.uid);
        const db = getDatabase();
        console.log('Database instance obtained');
        
        // Generate a UUID for the test result
        const testId = uuidv4();
        console.log('Generated test ID:', testId);
        
        const testResultsRef = ref(db, `users/${currentUser.uid}/grids/${testId}`);
        console.log('Database reference created:', testResultsRef);
        
        // Save directly to the generated UUID path instead of using push()
        await set(testResultsRef, testResult);
        console.log('Test result saved successfully with ID:', testId);
        
        // Navigate to test results page after successful save
        navigate('/test-results');
      } catch (dbError: unknown) {
        console.error('Firebase database error:', dbError);
        const errorMessage = dbError instanceof Error ? dbError.message : 'Failed to save test result';
        setError(`Database error: ${errorMessage}`);
        setLoading(false);
      }
    } catch (err: unknown) {
      console.error('Error saving test result:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save your test results';
      setError(`Error: ${errorMessage}. Please try again.`);
      setLoading(false);
    }
  };

  const handleOpenFullSizeView = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleShare = () => {
    const testData = {
      timestamp: Date.now(),
      eyeTested: eyeSelection,
      dots: dots,
      abnormalities: abnormalities.length > 0 ? abnormalities : [],
      notes: notes
    };
    shareTestResult(testData);
  };

  const handleDownload = () => {
    const testData = {
      timestamp: Date.now(),
      eyeTested: eyeSelection,
      dots: dots,
      abnormalities: abnormalities.length > 0 ? abnormalities : [],
      notes: notes
    };
    downloadTestResultImage(testData);
  };

  const handlePrint = () => {
    const testData = {
      timestamp: Date.now(),
      eyeTested: eyeSelection,
      dots: dots,
      abnormalities: abnormalities.length > 0 ? abnormalities : [],
      notes: notes
    };
    printTestResult(testData);
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#FFFFFF' }}>
              Select which eye you will test
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup 
                aria-label="eye-selection" 
                name="eye-selection" 
                value={eyeSelection} 
                onChange={handleEyeSelection}
              >
                <FormControlLabel 
                  value="left" 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center" sx={{ color: '#FFFFFF' }}>
                      <LeftEyeIcon sx={{ mr: 1 }} />
                      Left Eye
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value="right" 
                  control={<Radio />} 
                  label={
                    <Box display="flex" alignItems="center">
                      <RightEyeIcon sx={{ mr: 1 }} />
                      Right Eye
                    </Box>
                  } 
                />
              </RadioGroup>
            </FormControl>
            
            <Button 
              startIcon={<InfoIcon />}
              onClick={() => setInfoDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              Test Instructions
            </Button>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            height: { xs: 'calc(100vh - 180px)', sm: 'auto' }, // More space on mobile
            pb: 4 // Add padding at the bottom
          }}>
            <Typography gutterBottom sx={{ textAlign: 'center', maxWidth: 600, mb: 2, color: '#FFFFFF' }}>
              Cover your {eyeSelection === 'left' ? 'right' : 'left'} eye and focus on the center dot.
              If you notice any distortions, missing areas, or abnormalities in the grid, tap and hold those locations to create distortions.
            </Typography>
            
            <Box 
              sx={{ 
                width: '100%',
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                my: 1,
                flexGrow: 1,
                minHeight: { xs: '65vh', sm: '55vh', md: '65vh' }, // Increased minimum height
                mb: 3 // Add bottom margin
              }}
            >
              <Box 
                sx={{ 
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center'
              }}
            >
              <AmslerGridTest 
                onMarkPoint={handleMarkPoint} 
                markedPoints={dots}
                eye={eyeSelection as 'left' | 'right'}
              />
              </Box>
            
              <Box display="flex" justifyContent="center" mt={2} gap={2}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  onClick={handleResetPoints}
                  sx={{ borderRadius: 28, px: 3 }}
                >
                Reset Points
              </Button>
              </Box>
            </Box>
            
            <Alert severity="info" sx={{ maxWidth: 600, width: '100%', mb: 2 }}>
              You have marked {dots.length} points. If you don't see any abnormalities, you can proceed without marking any points. Tap and hold longer for stronger distortion effects.
            </Alert>
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#FFFFFF' }}>
              Describe what you experienced
            </Typography>
            
            {dots.length > 0 ? (
              <Box>
                <Typography gutterBottom sx={{ color: '#FFFFFF' }}>
                  You've marked {dots.length} {dots.length === 1 ? 'area' : 'areas'} on the grid. 
                  What abnormalities did you notice? (Select all that apply)
                </Typography>
                
                <FormControl component="fieldset" sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={abnormalityOptions.distortion}
                        onChange={handleAbnormalityChange('distortion')}
                      />
                    }
                    label="Distortion (lines appear bent or curved)"
                    sx={{ color: '#FFFFFF' }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={abnormalityOptions.blind_spot}
                        onChange={handleAbnormalityChange('blind_spot')}
                      />
                    }
                    label="Blind spot (missing or dark area)"
                    sx={{ color: '#FFFFFF' }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={abnormalityOptions.wavy_lines}
                        onChange={handleAbnormalityChange('wavy_lines')}
                      />
                    }
                    label="Wavy lines"
                    sx={{ color: '#FFFFFF' }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={abnormalityOptions.blurred_vision}
                        onChange={handleAbnormalityChange('blurred_vision')}
                      />
                    }
                    label="Blurred or fuzzy vision"
                    sx={{ color: '#FFFFFF' }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={abnormalityOptions.other}
                        onChange={handleAbnormalityChange('other')}
                      />
                    }
                    label="Other"
                    sx={{ color: '#FFFFFF' }}
                  />
                  {abnormalityOptions.other && (
                    <TextField
                      label="Please specify"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      value={otherAbnormality}
                      onChange={(e) => setOtherAbnormality(e.target.value)}
                    />
                  )}
                </FormControl>
              </Box>
            ) : (
              <Typography sx={{ color: '#FFFFFF' }}>
                You didn't mark any abnormalities on the grid. If your vision appears normal, you can proceed to the next step.
              </Typography>
            )}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ color: '#FFFFFF' }}>
              Additional Notes
            </Typography>
            <Typography gutterBottom sx={{ color: '#FFFFFF' }}>
              Add any additional information about your vision or test experience that might be relevant.
            </Typography>
            
            <TextField
              label="Notes (optional)"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              margin="normal"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              Your test results will be saved for future reference. You can view your test history any time.
            </Alert>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  // Modify the condition for showing the loading screen
  if (subscriptionLoading && !bypassLoading) {
    return (
      <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={40} sx={{ mb: 3 }} />
        <Typography variant="h6">Verifying subscription...</Typography>
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', maxWidth: 500, textAlign: 'center' }}>
          This should only take a few seconds. If it continues to load, please refresh the page.
        </Typography>
        <Button 
          variant="outlined" 
          sx={{ mt: 3 }}
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </Box>
    );
  } else if (!isPremium && !bypassLoading) {
    return (
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{ maxWidth: 600, width: '100%', mb: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LockIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Subscription Required
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              You need an active subscription to use the Amsler Grid test. 
              Regular eye testing can help detect changes in your vision early.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <AlertTitle>Subscribe on your mobile device</AlertTitle>
              For legal compliance with app store policies, please subscribe using the Amsler Grid Daily app 
              on your iOS or Android device. Web subscriptions will be available in a future update.
            </Alert>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
              <Button 
                variant="outlined" 
                startIcon={<AppleIcon />}
                sx={{ py: 1, px: 2 }}
                onClick={() => window.open('https://apps.apple.com/app/amslergriddaily', '_blank')}
              >
                iOS App
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<PhoneAndroidIcon />}
                sx={{ py: 1, px: 2 }}
                onClick={() => window.open('https://play.google.com/store/apps/details?id=com.amslergriddaily', '_blank')}
              >
                Android App
              </Button>
            </Box>
            
            <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
              A subscription gives you unlimited tests, history tracking, and the ability to share results with your doctor.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', pb: 4 }}>
      <Paper elevation={3} sx={{ 
        p: { xs: 1, sm: 2, md: 3 }, 
        mb: 4, 
        height: { xs: 'calc(100vh - 16px)', sm: 'auto' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible' // Prevent content from being cut off
      }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#FFFFFF' }}>
          Amsler Grid Test
        </Typography>
        <Typography variant="body2" color="text.primary" paragraph>
          {format(new Date(), 'MMMM d, yyyy')}
        </Typography>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box mt={2} sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'visible', // Prevent content from being cut off
          minHeight: { xs: '70vh', sm: 'auto' } // Ensure enough height on mobile
        }}>
          {getStepContent(activeStep)}
        </Box>
        
        <Box mt={4} display="flex" justifyContent="space-between">
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              'Submit'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </Paper>
      
      {/* Instructions Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
      >
        <DialogTitle>How to Take the Amsler Grid Test</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <ol style={{ paddingLeft: '20px' }}>
              <li>Make sure you are in a well-lit room.</li>
              <li>If you wear reading glasses, keep them on.</li>
              <li>Hold your device at a comfortable reading distance (about 12-15 inches from your face).</li>
              <li>Cover one eye completely (the one you're not testing).</li>
              <li>Focus on the center dot of the grid.</li>
              <li>While focusing on the center, pay attention to all parts of the grid.</li>
              <li>Tap and hold on any areas where lines appear wavy, broken, distorted, or missing.</li>
              <li>Continue until you've marked all the abnormalities you see.</li>
            </ol>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} color="primary">
            Understood
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Test Result Dialog for full-size view */}
      <TestResultDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        testData={{
          timestamp: Date.now(),
          eyeTested: eyeSelection,
          dots: dots.map(dot => ({
            x: dot.x,
            y: dot.y,
            timestamp: Date.now(),
            ...(dot.intensity !== undefined ? { intensity: dot.intensity } : {}),
            ...(dot.distortionRadius !== undefined ? { distortionRadius: dot.distortionRadius } : {})
          })),
          abnormalities: abnormalities.length > 0 ? abnormalities : [],
          notes: notes
        }}
        onShare={handleShare}
        onDownload={handleDownload}
        onPrint={handlePrint}
      />
    </Box>
  );
};

export default TestPage; 